// TaxRates.jsx — the GST rates in force, and what each one covers.
//
// ── WHY THIS SCREEN DOES NOT LET YOU EDIT ANYTHING ──
// The screen it replaces had Add, Edit, a status toggle and Delete. All four
// called endpoints that do not exist, and could not be made to exist, because
// the thing they edited does not: there is no TaxMaster table any more.
//
// A rate now comes from one of two places:
//
//   legacy — a percentage stored on the product itself, set on the product
//            form. Shown here as one row per distinct percentage.
//   rate   — a dated row in catalog.CfgTaxRates, which is in force between
//            validFrom and validTo. A rate is not switched off; it is
//            superseded by one starting the day the old one ends.
//
// So a toggle is meaningless (a rate is current by date, not by a flag), and a
// delete is worse than meaningless (it would orphan every product priced under
// it). The ids are synthetic keys like `legacy:0.00:NOTAX` and no write route
// accepts one.
//
// Offering four buttons that always fail teaches an admin the portal is
// unreliable. This shows what is true and says where each rate is actually
// changed.

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchTaxMasters } from "../../../slice/taxMaster/taxMasterSlice";
import { PageShell, DataTable, Pill, Button } from "../component/AdminUi";

const onDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

/* "in force since 1 Apr", "1 Apr – 31 Mar", or nothing dated at all. */
const period = (row) => {
  const from = onDate(row.validFrom);
  const to = onDate(row.validTo);
  if (!from && !to) return <span className="ui-muted">no end date</span>;
  if (from && !to) return `since ${from}`;
  if (!from && to) return `until ${to}`;
  return `${from} – ${to}`;
};

const pct = (n) =>
  n == null ? "—" : `${Number(n).toFixed(2).replace(/\.00$/, "")}%`;

export default function TaxRates() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { taxMasters, loading, error } = useSelector((s) => s.taxMaster);

  const [query, setQuery] = useState("");

  useEffect(() => {
    dispatch(fetchTaxMasters());
  }, [dispatch]);

  const rows = taxMasters || [];
  const shown = query.trim()
    ? rows.filter((r) => {
        const q = query.trim().toLowerCase();
        return (
          (r.name || "").toLowerCase().includes(q) ||
          String(r.percentage ?? "").includes(q) ||
          (r.hsn || "").toLowerCase().includes(q)
        );
      })
    : rows;

  const current = rows.filter((r) => r.isCurrent).length;

  return (
    <PageShell
      title="Tax rates"
      count={
        loading && !rows.length
          ? "Loading…"
          : `${rows.length} rate${rows.length === 1 ? "" : "s"} · ${current} in force` +
            (query ? ` · ${shown.length} matching` : "")
      }
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search name, rate or HSN",
      }}
      actions={
        <Button onClick={() => navigate("/admin/products")}>
          Go to products
        </Button>
      }
    >
      {/* Said once, at the top, rather than discovered by pressing a button
          that does nothing. */}
      <div className="ui-alert ui-alert--note">
        <span>
          Rates are not edited here. A <strong>legacy</strong> rate is the
          percentage on the product itself — change it on the product. A{" "}
          <strong>dated</strong> rate runs between its own dates and is replaced
          by a new one starting when it ends, never switched off.
        </span>
      </div>

      <DataTable
        columns={["Rate", "Name", "Cess", "HSN", "In force", "Products", "Source"]}
        loading={loading}
        error={error}
        isEmpty={!loading && shown.length === 0}
        emptyTitle={query ? "No rate matches that" : "No rates configured"}
        emptyHint={
          query
            ? "Try the percentage on its own."
            : "Products priced without a rate are treated as zero-rated."
        }
      >
        {shown.map((row) => (
          <tr key={row.id}>
            <td className="ui-num">
              <strong style={{ fontSize: 15 }}>{pct(row.percentage)}</strong>
            </td>
            <td>
              <span className="ui-who__name">{row.name || "—"}</span>
            </td>
            <td className="ui-num">
              {row.cessPercentage ? (
                pct(row.cessPercentage)
              ) : (
                <span className="ui-muted">none</span>
              )}
            </td>
            <td className="ui-num">
              {row.hsn || <span className="ui-muted">not set</span>}
            </td>
            <td>
              {row.isCurrent ? (
                <Pill tone="forest">{period(row)}</Pill>
              ) : (
                <span className="ui-muted">{period(row)}</span>
              )}
            </td>
            <td className="ui-num">
              {row.productCount != null ? (
                row.productCount
              ) : (
                <span className="ui-muted">—</span>
              )}
            </td>
            <td>
              <Pill tone={row.source === "legacy" ? "grey" : "gold"}>
                {row.source === "legacy" ? "On the product" : "Dated rate"}
              </Pill>
            </td>
          </tr>
        ))}
      </DataTable>
    </PageShell>
  );
}
